import Breadcrumb from '@/components/breadcrumb';
import AlgorithmSelector from '@components/algorithm-selector.tsx';
import { FileSelector } from '@components/file-selector.tsx';
import PresetSelector from '@components/preset-selector.tsx';
import {
  IStationSwitcherContext,
  StationSwitcherContext,
} from '@components/providers/station/context';
import RouteTimeSelector from '@components/route-time-selector.tsx';
import ServiceTimeSelector from '@components/service-time-selector.tsx';
import SimulationTime from '@components/simulation-time.tsx';
import UniverseMode from '@components/universe-mode.tsx';
import { VehiclesCapacities } from '@components/vehicle-capacities.tsx';
import VersionSelector from '@components/version-selector.tsx';
import { vehicles_joins } from '@helpers/constants';
import { algorithms, calculation_mode } from '@helpers/types.ts';
import { useDownload } from '@hooks/use-download.ts';
import {
  ArrowRightAlt,
  CloudDownload,
  Error,
  HelpOutline,
  PlayArrow,
  Shuffle,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingDialog } from '@sections/configure-plan/loading-dialog.tsx';
import { MetricsTable } from '@sections/configure-plan/metrics-table.tsx';
import { useCallback, useContext } from 'react';
import { useConfigurePlan } from './use-configure-plan';

export const ConfigurePlan = () => {
  const { destStation: station } = useContext<IStationSwitcherContext>(
    StationSwitcherContext,
  );
  const hooks = useConfigurePlan(station);
  const { downloadXlsx } = useDownload();

  const onDownload = useCallback(() => {
    if (hooks.metrics?.out_zones) {
      downloadXlsx(
        hooks.metrics.out_zones.map((x) => ({
          tracking_id: x,
        })),
        'ordenes-sin-zona.xlsx',
      );
    }
  }, [hooks.metrics, downloadXlsx]);

  return (
    <div className="flex flex-col h-full overflow-auto gap-4">
      <Breadcrumb
        sections={[
          { label: 'Simulaciones', link: '/' },
          { label: 'Configuración' },
        ]}
        description="Configuración de nueva simulación"
      />
      <LoadingDialog message={hooks.loadingMessage} />
      <div className="mb-8">
        <Container sx={{ padding: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 5, xl: 5 }}>
              <Paper variant="outlined" className="p-4">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ArrowRightAlt color="success" />
                      <Typography variant="h6" fontWeight="bold">
                        Planificador de rutas
                      </Typography>
                    </Stack>
                  </div>
                  <div className="col-span-12 grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-7">
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        autoComplete="off"
                        label="Nombre del plan"
                        onChange={hooks.onChangePlanName}
                        value={hooks.plan_name}
                      />
                    </div>
                    <div className="col-span-4">
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        autoComplete="off"
                        label="Prefijo"
                        onChange={hooks.onChangePrefix}
                        value={hooks.prefix}
                      />
                    </div>
                    <div className="col-span-1">
                      <IconButton onClick={hooks.onRandomizeName} size="small">
                        <Shuffle fontSize="small" />
                      </IconButton>
                    </div>
                  </div>

                  {/*<div className="col-span-12">
                    <Typography variant="body2" gutterBottom>
                      Ingesta de datos
                    </Typography>
                    <CalculationMode
                      onChange={hooks.onChangeMode}
                      value={hooks.mode}
                    />
                  </div>*/}

                  {hooks.mode === calculation_mode.automatic ? (
                    <div className="col-span-12">
                      <Typography variant="body2" gutterBottom>
                        Universo de datos
                      </Typography>
                      <UniverseMode
                        country={station?.country || ''}
                        station={station?.station || ''}
                        onChange={hooks.onChangeUniverseMode}
                        value={hooks.universe}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="col-span-12">
                        <FileSelector
                          value={hooks.file}
                          types={['xlsx']}
                          onChangeFile={hooks.onChangeFile}
                        />
                      </div>
                      <div className="col-span-12">
                        <div className="flex justify-end">
                          <Button
                            target="_blank"
                            component={'a'}
                            variant="outlined"
                            size="small"
                            href={'/template.xlsx'}
                          >
                            Plantilla
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  {/*<div className="col-span-12">
                    <div className="flex justify-start">
                      <SizeSelector
                        value={hooks.sizes}
                        onChange={hooks.onChangeSizes}
                      />
                    </div>
                  </div>*/}

                  <div className="col-span-6">
                    <VersionSelector
                      country={station?.country || ''}
                      station={station?.station || ''}
                      value={hooks.version}
                      onChange={hooks.onChangeVersion}
                    />
                  </div>
                  <div className="col-span-6">
                    <AlgorithmSelector
                      value={hooks.algorithm}
                      onChange={hooks.onChangeAlgorithm}
                    />
                  </div>
                  <div className="col-span-12">
                    <PresetSelector
                      value={hooks.preset}
                      onChange={hooks.onChangePreset}
                    />
                  </div>

                  <div className="col-span-12">
                    <div className="text-sm font-bold mb-2">
                      Vehículos disponibles para todas las zonas elegidas
                    </div>
                    <VehiclesCapacities
                      hideTitle={true}
                      value={hooks.vehicles}
                      vehicles={vehicles_joins}
                      onChange={hooks.onChangeVehicles}
                    />
                  </div>

                  {hooks.algorithm === algorithms.k_means && (
                    <div className="col-span-12">
                      <RouteTimeSelector
                        value={hooks.routeTime}
                        onChange={hooks.onChangeRouteTime}
                      />
                    </div>
                  )}

                  {hooks.algorithm === algorithms.or_tools && (
                    <div className="col-span-12 grid grid-cols-12 gap-4">
                      <div className="col-span-12">
                        <Slider
                          marks
                          min={0}
                          max={3}
                          step={1}
                          value={hooks.fusion}
                          defaultValue={hooks.fusion}
                          valueLabelDisplay="auto"
                          onChange={hooks.onChangeFusion}
                        />
                        <Typography variant="caption">
                          <Tooltip
                            title={'Número de zonas adyacentes a fusionar'}
                          >
                            <IconButton size="small">
                              <HelpOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          Zonas de fusión [ {hooks.fusion} ]{' '}
                        </Typography>
                      </div>

                      <div className="col-span-4">
                        <SimulationTime
                          disabled={true}
                          value={hooks.simulationTime}
                          onChange={hooks.onChangeSimulationTime}
                        />
                      </div>
                      <div className="col-span-4">
                        <RouteTimeSelector
                          value={hooks.routeTime}
                          onChange={hooks.onChangeRouteTime}
                        />
                      </div>
                      <div className="col-span-4">
                        <ServiceTimeSelector
                          value={hooks.service_time}
                          onChange={hooks.onChangeServiceTime}
                        />
                      </div>
                      {/* Presets and Max Distance in a new row */}
                      <div className="col-span-12 grid grid-cols-12 gap-2">
                        <div className="col-span-12">
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="number"
                            label="Dist. máx. ruta"
                            value={hooks.max_distance}
                            onChange={(e) =>
                              hooks.onChangeMaxDistance(Number(e.target.value))
                            }
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  m
                                </InputAdornment>
                              ),
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="col-span-12">
                    <LoadingButton
                      fullWidth
                      disableElevation
                      color="success"
                      variant="contained"
                      onClick={hooks.onVerifyPlan}
                      loading={hooks.preloadPlan}
                      disabled={
                        hooks.universe.length === 0 ||
                        !hooks.sizes ||
                        !hooks.version ||
                        !hooks.isValidVehicles()
                      }
                      sx={{ height: 48 }}
                    >
                      Validar
                    </LoadingButton>
                  </div>
                </div>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 7, xl: 7 }}>
              <Paper variant="outlined" className="p-4" sx={{ height: '100%' }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="h6" fontWeight="bold">
                      Órdenes en planificación
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Revisa y confirma las órdenes cargadas
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <MetricsTable metrics={hooks.metrics} />
                  </Grid>

                  {hooks.metrics && (
                    <Grid size={{ xs: 12 }}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, bgcolor: 'action.hover', border: 'none' }}
                      >
                        <Stack direction="row" justifyContent="space-between">
                          <div>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Resumen
                            </Typography>
                            <Stack direction="row" spacing={4} mt={1}>
                              <div>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Total de órdenes
                                </Typography>
                                <Typography variant="h5" fontWeight="bold">
                                  {hooks.metrics.total}
                                </Typography>
                              </div>
                              <div>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Cantidad de zonas
                                </Typography>
                                <Typography variant="h5" fontWeight="bold">
                                  {hooks.metrics.zones?.length || 0}
                                </Typography>
                              </div>
                            </Stack>
                          </div>
                        </Stack>
                      </Paper>
                    </Grid>
                  )}

                  <Grid size={{ xs: 12 }}>
                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent="space-between"
                    >
                      <Button
                        fullWidth
                        variant="outlined"
                        color="inherit"
                        startIcon={<CloudDownload />}
                        disabled={!hooks.metrics}
                        sx={{ borderRadius: 5, textTransform: 'none' }}
                        onClick={onDownload}
                      >
                        Descargar
                      </Button>
                      <LoadingButton
                        fullWidth
                        variant="contained"
                        // @ts-ignore
                        color="success"
                        disableElevation
                        startIcon={<PlayArrow />}
                        loading={hooks.simulatingPlan}
                        onClick={hooks.onPrepareSimulation}
                        disabled={!hooks.metrics}
                        sx={{ borderRadius: 5, textTransform: 'none' }}
                      >
                        Iniciar simulación
                      </LoadingButton>
                    </Stack>
                  </Grid>

                  {hooks.error && (
                    <Grid size={{ xs: 12 }}>
                      <Alert severity="warning" icon={<Error />}>
                        {hooks.error}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </div>
    </div>
  );
};
